import { z } from "zod";
/**
 * Player race/faction in the game
 */
export const PlayerRaceSchema = z.enum(['humans', 'robots', 'axons']);
/**
 * All researchable sciences. Sciences are player-wide (not per-planet).
 */
export const ScienceIdSchema = z.enum([
    // Fundamental
    'mathematics', 'physics', 'chemistry', 'astronomy', 'ecology',
    // Engineering
    'architecture', 'signalSystems', 'rockets', 'aerodynamics',
    'wormholes', 'gravitationalManipulators',
    // Warfare
    'intelligence', 'defenceSystems', 'laser', 'ion', 'plasma',
    'polymerShields', 'carbonFiberShields', 'heavyMetalShields',
    // Warfare tactics (only one may be researched per player)
    'offensiveTactics', 'defensiveTactics', 'surpriseTactics',
]);
/**
 * Premium tier levels. The player's actual tier is stored (with its expiry)
 * as `premium.tier` — a `PremiumTierGrant` inside the server-owned
 * `PlayerPremium` object (`universes/{u}/players/{uid}.premium`), never on the
 * client-writable `users/{uid}` doc where a player could self-grant it. There
 * is no standalone top-level `premiumTier` field — it was merged into
 * `premium.tier` (see `premium.schemas.ts`).
 */
export const PremiumTierSchema = z.enum(['normal', 'premium', 'platinum']);
export const PlayerSciencesSchema = z.object({
    mathematics: z.number().int().min(0).max(10),
    physics: z.number().int().min(0).max(10),
    chemistry: z.number().int().min(0).max(15),
    astronomy: z.number().int().min(0).max(15),
    ecology: z.number().int().min(0).max(15),
    architecture: z.number().int().min(0).max(10),
    signalSystems: z.number().int().min(0).max(15),
    rockets: z.number().int().min(0).max(15),
    aerodynamics: z.number().int().min(0).max(15),
    wormholes: z.number().int().min(0).max(15),
    gravitationalManipulators: z.number().int().min(0).max(1),
    intelligence: z.number().int().min(0).max(20),
    defenceSystems: z.number().int().min(0).max(20),
    laser: z.number().int().min(0).max(15),
    ion: z.number().int().min(0).max(15),
    plasma: z.number().int().min(0).max(15),
    polymerShields: z.number().int().min(0).max(10),
    carbonFiberShields: z.number().int().min(0).max(10),
    heavyMetalShields: z.number().int().min(0).max(10),
    offensiveTactics: z.number().int().min(0).max(10),
    defensiveTactics: z.number().int().min(0).max(10),
    surpriseTactics: z.number().int().min(0).max(10),
});
/**
 * Ship upgrade levels, keyed by numeric `ShipId` (as a string — zod records
 * require string keys). Only upgradable ship IDs ever get an entry
 * (`UPGRADABLE_SHIP_IDS` in `constants/ships.ts`); a missing key means level
 * 0, same convention as `PlanetShips`. Lives on the server-owned players doc
 * (`universes/{u}/players/{uid}.shipUpgrades`), never on the client-writable
 * `users/{uid}` doc — same reasoning as `PremiumTier` above: this is
 * server-authoritative progress a client must not be able to self-grant.
 */
export const PlayerShipUpgradesSchema = z.record(z.string(), z.number().int().min(0).max(10));
/**
 * Schema for validating user profile creation
 */
export const CreateUserSchema = z.object({
    displayName: z.string().min(3).max(50),
    email: z.string().email(),
    race: PlayerRaceSchema.optional(),
});
export const EmailSchema = z.string().trim().email('Enter a valid email address');
/** Firebase accepts 6+ characters; sign-up asks for 8. Sign-in only requires non-empty so older accounts keep working. */
export const PasswordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be at most 128 characters');
export const EmailOnlySchema = z.object({
    email: EmailSchema,
});
export const PasswordSignInSchema = z.object({
    email: EmailSchema,
    password: z.string().min(1, 'Enter your password'),
});
export const PasswordSignUpSchema = z
    .object({
    email: EmailSchema,
    password: PasswordSchema,
    confirmPassword: z.string(),
})
    .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
/**
 * Schema for validating user settings updates
 */
export const UpdateUserSettingsSchema = z.object({
    language: z.string().optional(),
    emailNotifications: z.boolean().optional(),
    inGameNotifications: z.boolean().optional(),
});
/**
 * Schema for validating a displayName (nickname) change. Same bounds as
 * `CreateUserSchema.displayName`. Also denormalized onto the server-owned
 * `players/{uid}` doc and the public `universes/{u}/rankings/{uid}` doc —
 * see `updateDisplayName` in `apps/functions/src/index.ts`.
 */
export const UpdateDisplayNameSchema = z.object({
    displayName: z.string().trim().min(3).max(50),
});
/**
 * Schema for validating a profile photo upload — a base64 JPEG data URL
 * produced client-side by `resizeImageToSquareJpeg`. The resulting photoURL
 * is denormalized onto the server-owned `players/{uid}` doc and the public
 * `universes/{u}/rankings/{uid}` doc — see `uploadProfilePhoto` in
 * `apps/functions/src/index.ts`.
 */
export const UploadProfilePhotoSchema = z.object({
    photoData: z.string().regex(/^data:image\/jpeg;base64,[A-Za-z0-9+/]+=*$/, 'photoData must be a base64 JPEG data URL'),
});
/**
 * Schema for validating a tutorial progress update. Lives on the
 * server-owned players doc (`universes/{u}/players/{uid}`) — same reasoning
 * as `PremiumTierSchema` above: a client-writable `users/{uid}` copy would
 * let a player forge "tutorial finished" state. See `updateTutorialProgress`
 * in `apps/functions/src/index.ts`.
 */
export const UpdateTutorialProgressSchema = z.object({
    stepIndex: z.number().int().min(0),
    disabled: z.boolean(),
    /** The planet on screen; the server replays it to check the target step can be reached. */
    planetId: z.string().min(1).optional(),
});
/**
 * Schema for validating user profile data from Firestore (`users/{uid}` —
 * global identity/settings only, shared across every universe the player
 * has joined). Per-universe progress (`race`, `mainPlanetId`, `sciences`,
 * `shipUpgrades`, `points`, `premiumTier`, tutorial progress) lives
 * exclusively on the server-owned `universes/{u}/players/{uid}` doc, never
 * here — this doc is fully client-read/write (see `firestore.rules`), so
 * anything gameplay-authoritative must not be declared on it. There is
 * deliberately no `universeId` field either (issue #53): a player can hold
 * progress in more than one universe at once, so "the" universe isn't a
 * property of the user — it's the current session's pick, tracked
 * client-side by `useUniverseStore` (localStorage-backed) and sent as
 * `universeId` on every request instead.
 */
export const UserProfileSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    /** Empty for guest (anonymous) accounts until they upgrade to a real sign-in method. */
    email: z.string().email().or(z.literal('')),
    createdAt: z.date(),
    lastLoginAt: z.date(),
    allianceId: z.string().optional(),
    /**
     * Resized (256x256) profile photo. Uploaded via the `uploadProfilePhoto`
     * Cloud Function (server writes to Storage with the Admin SDK) — see
     * `apps/client/src/services/profile.service.ts`.
     */
    photoURL: z.string().url().optional(),
    settings: z.object({
        language: z.string(),
        emailNotifications: z.boolean(),
        inGameNotifications: z.boolean(),
    }),
});
//# sourceMappingURL=user.schemas.js.map